import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Table,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { toast } from 'react-toastify';
import PaginationComponent from '../../Components/Common/Pagination';
import {
  requestPresignedUrl,
  uploadFileToS3,
  fetchAISummary,
  fetchUploadedFiles,
  deleteFile,
  downloadFile
} from '../../slices/pdfSummarization/thunk';
import { resetUpload, clearError } from '../../slices/pdfSummarization/reducer';

const Documents = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // Redux state using useSelector
  const {
    uploadedFiles,
    totalCount,
    currentFile,
    aiSummary,
    preSignedData,
    loading,
    listLoading,
    deleteLoading,
    summaryLoading,
    error
  } = useSelector((state) => state.Pdf);

  // Local state
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);

  // Set document title
  document.title = "Beacon Chat Bot | Knowledge Base";

  // Clear any stale errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Fetch uploaded files on component mount
  useEffect(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
  }, [dispatch, currentPage]);

  // Display error alerts (only show if not loading to avoid showing stale errors)
  useEffect(() => {
    if (error && !listLoading) {
      toast.error(error);
    }
  }, [error, listLoading]);

  // Toggle delete modal
  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (deleteModal) {
      setFileToDelete(null);
    }
  };

  // Toggle view modal
  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (viewModal) {
      setSelectedPdf(null);
    }
  };

  // Handle delete click
  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (fileToDelete) {
      try {
        const data ={
          document_id: fileToDelete.document_id,
          user_id: fileToDelete?.user_id
        }
        await dispatch(deleteFile(data)).unwrap();
        toast.success(`"${fileToDelete.file_name}" deleted successfully`);

        // Check if this was the last item on the current page
        const remainingItemsOnPage = uploadedFiles.length - 1;

        // If no items remain on current page and we're not on page 1, go to previous page
        if (remainingItemsOnPage === 0 && currentPage > 1) {
          const newPage = currentPage - 1;
          setCurrentPage(newPage);
          const offset = (newPage - 1) * itemsPerPage;
          dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
        } else {
          // Otherwise, just refresh the current page
          const offset = (currentPage - 1) * itemsPerPage;
          dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
        }

        toggleDeleteModal();
      } catch (error) {
        toast.error('Failed to delete file. Please try again.');
      }
    }
  };


  // Handle upload button click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection and upload
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.warning('Please select a PDF file');
      event.target.value = '';
      return;
    }

    // Validate file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.warning('File size must be less than 10MB');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    dispatch(resetUpload());

    try {
      // Step 1: Request pre-signed URL
      toast.info(`Uploading "${file.name}"...`);

      const urlResult = await dispatch(
        requestPresignedUrl({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        })
      ).unwrap();

      // Step 2: Upload to S3
      await dispatch(
        uploadFileToS3({
          file,
          preSignedUrl: urlResult.preSignedUrl
        })
      ).unwrap();

      toast.success('File uploaded successfully! Processing...');

      // Refresh file list
      const offset = (currentPage - 1) * itemsPerPage;
      dispatch(fetchUploadedFiles({ itemsPerPage, offset }));

    } catch (error) {
      const errorMessage = error || 'Upload failed. Please try again.';
      toast.error(errorMessage);
    }

    // Reset file input
    event.target.value = '';
  };

  // Handle file download
  const handleDownloadFile = async (fileId, fileName) => {

    if (!fileId) {
      toast.error('Invalid file ID');
      return;
    } else {
      window.open(fileId, '_blank', 'noopener,noreferrer')
    }
  };

  // Handle view file (fetch summary and open modal)
  const handleViewFile = async (file) => {
    if (file.document_id) {
      setSelectedPdf(file);
      setViewModal(true);

      try {
        await dispatch(
          fetchAISummary({ correlationId: file.document_id })
        ).unwrap();
      } catch (error) {
        toast.error('Failed to fetch summary');
      }
    } else {
      toast.warning('No correlation ID found for this file');
    }
  };

  // Copy summary to clipboard
  const handleCopySummary = () => {
    if (aiSummary?.summary?.summary) {
      navigator.clipboard.writeText(aiSummary.summary?.summary);
      toast.success('Summary copied to clipboard!');
    }
  };

  // Export summary as text file
  const handleExportSummary = () => {
    if (aiSummary?.summary?.summary) {
      const blob = new Blob([aiSummary.summary?.summary], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ai-summary.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Summary exported successfully!');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const colors = {
      pending_upload: 'warning',
      processing: 'info',
      uploaded: 'success',
      failed: 'danger',
    };
    return <Badge color={colors[status] || 'secondary'}>{getStatusMessage(status)}</Badge>;
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'pending_upload':
        return 'Pending';
      case 'uploaded':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case "failed":
        return 'Failed';
      default:
        return 'Unknown status';
    }
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const offset = (pageNumber - 1) * itemsPerPage;
    dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const detIStTimemp = (dateStr) => {
    const utcDate = new Date(dateStr);

    // Convert to IST (UTC +5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // milliseconds
    const istDate = new Date(utcDate.getTime() + istOffset);

    // Format the output
    const formatted = istDate.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false
    });
    return formatted;
  }

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          {/* Main Content Area */}
          <Row>
            <Col>
              <div className="d-flex gap-2 mb-3">
                {/* Upload Button */}
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <>
                      <i className="ri-upload-line me-2"></i> Upload PDF
                    </>
                  )}
                </Button>

                {/* Refresh Button */}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    const offset = (currentPage - 1) * itemsPerPage;
                    dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
                    // toast.info('Refreshing file list...');
                  }}
                  disabled={listLoading || loading}
                >

                  <>
                    <i className="ri-refresh-line me-2"></i> Refresh
                  </>

                </Button>

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf"
                  style={{ display: 'none' }}
                />
              </div>

            </Col>
          </Row>

          <Row>
            {/* List Uploaded Files */}
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">List Uploaded Files</h5>
                </CardHeader>
                <CardBody>
                  {/* Show loading spinner when fetching data */}
                  {listLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading files...</p>
                    </div>
                  ) : uploadedFiles && uploadedFiles.length > 0 ? (
                    <>
                      {/* Table */}
                      <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>File Name</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadedFiles.map((file) => (
                              <tr key={file.document_id}>
                                <td>
                                  <div>
                                    <i className="ri-file-pdf-line text-danger me-2"></i>
                                    <span className="fw-medium">{file.name || file.file_name}</span>
                                  </div>
                                  <small className="text-muted">
                                    {detIStTimemp(file.updated_at)}
                                    {/* {file.file_size} • {file.updated_at} */}
                                  </small>
                                </td>
                                <td>{getStatusBadge(file?.status)}</td>
                                <td>
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      href="#"
                                      className="btn btn-soft-secondary btn-sm dropdown"
                                      tag="button"
                                    >
                                      <i className="ri-more-fill align-middle"></i>
                                    </DropdownToggle>
                                    <DropdownMenu
                                      className="dropdown-menu-end"
                                      container="body"
                                      style={{ zIndex: 1050 }}
                                    >
                                      {/* <DropdownItem
                                        onClick={() => handleViewFile(file)}
                                        disabled={file.status === 'processing' || file.status === 'pending_upload'}
                                      >
                                        <i className="ri-eye-line me-2"></i>
                                        View Summary
                                      </DropdownItem> */}
                                      {/* <DropdownItem 
                                        onClick={() => handleDownloadFile(file.s3_pdf_url, file.file_name)}
                                        disabled={file.status === 'processing' || file.status === 'pending_upload'}
                                      >
                                        <i className="ri-download-line me-2"></i>
                                        Download
                                      </DropdownItem> */}
                                      <DropdownItem divider />
                                      <DropdownItem
                                        onClick={() => handleDeleteClick(file)}
                                        className="text-danger"
                                      >
                                        <i className="ri-delete-bin-line me-2"></i>
                                        Delete
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {/* Pagination Info */}
                      <Row className="mt-3">
                        <Col>
                          <p className="text-muted mb-0">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                            {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                            {totalCount} entries
                          </p>
                        </Col>
                      </Row>

                      {/* Pagination - Only show if there are multiple pages */}
                      {totalPages > 1 && (
                        <PaginationComponent
                          currentPage={currentPage}
                          totalPages={totalPages}
                          handlePageChange={handlePageChange}
                        />
                      )}
                    </>
                  ) : (
                    /* Empty State */
                    <div className="text-center py-5">
                      <i
                        className="ri-file-pdf-2-line text-muted"
                        style={{ fontSize: '3rem' }}
                      ></i>
                      <p className="text-muted mt-3 mb-1">No files uploaded yet</p>
                      <p className="text-muted small">
                        Upload a PDF file to see it listed here
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered backdrop="static">
          <ModalHeader toggle={toggleDeleteModal}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            <div className="text-center">
              <i className="ri-delete-bin-line text-danger" style={{ fontSize: '3.5rem' }}></i>
              <h5 className="mt-3 mb-2">Are you sure?</h5>
              <p className="text-muted mb-1">
                Do you want to delete this file?
              </p>
              {fileToDelete && (
                <div className="mt-3 p-3 bg-light rounded">
                  <div className="d-flex align-items-center justify-content-center">
                    <i className="ri-file-pdf-line text-danger me-2"></i>
                    <strong>{fileToDelete.file_name || fileToDelete.name}</strong>
                  </div>
                </div>
              )}
              <p className="text-muted mt-3 mb-0">
                <small>This action cannot be undone.</small>
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleDeleteModal} disabled={deleteLoading}>
              <i className="ri-close-line me-1"></i>
              Cancel
            </Button>
            <Button color="danger" onClick={confirmDelete} disabled={deleteLoading}>
              <i className="ri-delete-bin-line me-1"></i>
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Documents;
